class DetailsModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.detailsContainer?.querySelector('summary');

    if (!this.detailsContainer || !this.summaryToggle) return;

    this._closeTimer = null;
    this._closeToken = 0;
    this._scrollY = 0;
    this._lastTouchY = 0;
    this._touchStartHandler = this.onTouchStart.bind(this);
    this._touchMoveHandler = this.onTouchMove.bind(this);

    this.detailsContainer.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.close();
    });

    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));

    // On touch devices, handle the tap at touchend and prevent the ghost click
    // that iOS/Android fires ~300 ms later. Without this, every 3rd tap on the
    // summary re-fires as a click while the modal is already open → auto-close.
    let _tapStartY = 0;
    this.summaryToggle.addEventListener('touchstart', (e) => {
      _tapStartY = e.touches[0]?.clientY ?? 0;
    }, { passive: true });
    this.summaryToggle.addEventListener('touchend', (e) => {
      const dy = Math.abs((e.changedTouches[0]?.clientY ?? 0) - _tapStartY);
      if (dy > 8) return; // ignore scroll gestures
      e.preventDefault(); // ← kills the ghost click; onSummaryClick won't fire
      this._handleSummaryToggle();
    });

    const closeButton =
      this.querySelector('.modal__close-button') ||
      this.querySelector('.search-modal__close-button');

    if (closeButton) {
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      });
    }

    this.summaryToggle.setAttribute('role', 'button');

    // Close when clicking the transparent backdrop area of the search modal
    // (.search-modal is position:fixed full-screen, so clicks below the white
    // content area land on .search-modal itself, not on elements outside details-modal)
    const searchModalEl = this.detailsContainer.querySelector('.search-modal');
    if (searchModalEl) {
      searchModalEl.addEventListener('click', (event) => {
        if (!this.isOpen()) return;
        if (this._openTime && Date.now() - this._openTime < 400) return;
        if (!event.target.closest('.search-modal__content')) {
          this.close(false);
        }
      });
    }

    this._outsideClick = (event) => {
      if (!this.isOpen()) return;
      // Floating menu search button manages open/close itself — don't interfere
      if (event.target.closest('[data-nav="search"]')) return;
      // Block ghost clicks that fire ~300ms after the tap that opened the modal
      if (this._openTime && Date.now() - this._openTime < 400) return;
      if (this.contains(event.target)) return;
      this.close(false);
    };

    document.addEventListener('click', this._outsideClick, true);
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  // Called from touchend — ghost click already prevented by e.preventDefault()
  _handleSummaryToggle() {
    if (this._closeTime && Date.now() - this._closeTime < 400) return;
    this.isOpen() ? this.close() : this.open();
  }

  // Called from click — desktop only (touch fires touchend.preventDefault() above)
  onSummaryClick(event) {
    event.preventDefault();
    event.stopPropagation();
    // Fallback time guards for any browser that still fires click after touchend
    const now = Date.now();
    if (this._closeTime && now - this._closeTime < 400) return;
    if (this._openTime && now - this._openTime < 400) return;
    this.isOpen() ? this.close() : this.open();
  }

  open() {
    this._openTime = Date.now();
    // Cancel old pending close
    this._closeToken++;
    clearTimeout(this._closeTimer);

    const searchModal = this.detailsContainer.querySelector('.search-modal');
    if (searchModal) {
      searchModal.classList.remove('search-modal--closing');
    }

    this.detailsContainer.setAttribute('open', true);
    this.summaryToggle.setAttribute('aria-expanded', 'true');

    this.lockBodyScroll();

    const focusContainer = this.detailsContainer.querySelector('[tabindex="-1"]');
    if (focusContainer) {
      trapFocus(focusContainer);
    }

    // Show recent searches / trending without auto-focusing the input
    const predictiveSearch = this.detailsContainer.querySelector('predictive-search');
    if (predictiveSearch && typeof predictiveSearch.openEmptyState === 'function') {
      predictiveSearch.openEmptyState();
    }
  }

  close(focusToggle = true) {
    if (!this.isOpen()) return;

    const searchModal = this.detailsContainer.querySelector('.search-modal');
    const token = ++this._closeToken;

    if (!searchModal) {
      this._finalizeClose(focusToggle);
      return;
    }

    searchModal.classList.add('search-modal--closing');

    clearTimeout(this._closeTimer);

    const finish = () => {
      if (token !== this._closeToken) return;

      this._finalizeClose(focusToggle);
      searchModal.classList.remove('search-modal--closing');
    };

    this._closeTimer = setTimeout(finish, 300);

    searchModal.addEventListener(
      'animationend',
      () => {
        clearTimeout(this._closeTimer);
        finish();
      },
      { once: true }
    );
  }

  _finalizeClose(focusToggle = true) {
    // Stamp close time FIRST so the onSummaryClick guard is active before any
    // focus event fires. Remove 'open' before removeTrapFocus so that the
    // phantom tap iOS fires from summaryToggle.focus() sees isOpen() === false.
    this._closeTime = Date.now();
    this.detailsContainer.removeAttribute('open');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
    this.unlockBodyScroll();
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
  }

  lockBodyScroll() {
    if (document.body.classList.contains('search-scroll-locked')) return;

    this._scrollY = window.scrollY;

    document.documentElement.classList.add('search-open');
    document.body.classList.add('overflow-hidden', 'search-open', 'search-scroll-locked');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    document.addEventListener('touchstart', this._touchStartHandler, { passive: true });
    document.addEventListener('touchmove', this._touchMoveHandler, { passive: false });
  }

  unlockBodyScroll() {
    document.documentElement.classList.remove('search-open');
    document.body.classList.remove('overflow-hidden', 'search-open', 'search-scroll-locked');

    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';

    document.removeEventListener('touchstart', this._touchStartHandler);
    document.removeEventListener('touchmove', this._touchMoveHandler);
  }

  onTouchStart(event) {
    this._lastTouchY = event.touches?.[0]?.clientY || 0;
  }

  onTouchMove(event) {
    if (!document.body.classList.contains('search-open')) return;

    const modalContent = event.target.closest('.search-modal__content');

    // Touch outside the search modal — block completely
    if (!modalContent) {
      event.preventDefault();
      return;
    }

    const currentY = event.touches?.[0]?.clientY || 0;
    const deltaY = currentY - this._lastTouchY;
    this._lastTouchY = currentY;

    // Walk up from the touch target to find the innermost scrollable element
    // inside the modal (e.g. .predictive-search results, .search-empty-state)
    let scrollEl = event.target;
    while (scrollEl && scrollEl !== modalContent) {
      if (scrollEl.scrollHeight > scrollEl.clientHeight) break;
      scrollEl = scrollEl.parentElement;
    }
    if (!scrollEl) scrollEl = modalContent;

    const canScroll = scrollEl.scrollHeight > scrollEl.clientHeight;

    if (!canScroll) {
      event.preventDefault();
      return;
    }

    const atTop = scrollEl.scrollTop <= 0;
    const atBottom = Math.ceil(scrollEl.scrollTop + scrollEl.clientHeight) >= scrollEl.scrollHeight;

    // iOS scroll chaining stop — prevent bounce at boundaries
    if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
      event.preventDefault();
    }
  }
}

customElements.define('details-modal', DetailsModal);